import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { planningApi as P, JOB_STATUS_STYLE, PRIORITY_STYLE } from "@/lib/planning";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, CheckCircle2, Rocket, XCircle, Factory } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const canEdit = can("production_planning", "edit");

  const load = async () => {
    setLoading(true);
    try { setJob(await P.job(id)); } catch { toast.error("Not found"); navigate("/production-planning"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const move = async (status, note = null) => {
    try { setJob(await P.setStatus(id, status, note)); toast.success(status === "Released" ? "Released to Production" : `Marked ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const submitCancel = async () => {
    if (!reason) { toast.error("Enter a reason"); return; }
    await move("Cancelled", reason); setCancelOpen(false); setReason("");
  };

  if (loading || !job) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  const a = job.artwork;

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <button onClick={() => navigate("/production-planning")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to planning</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{job.job_no}</h2>
            <Badge className={`hover:bg-inherit ${JOB_STATUS_STYLE[job.status]}`} data-testid="job-status-badge">{job.status}</Badge>
            <Badge className={`hover:bg-inherit ${PRIORITY_STYLE[job.priority]}`}>{job.priority}</Badge>
            {job.published && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Published to Production</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500">{job.customer?.company_name} · from {job.so_no} · planner {job.planner_name}</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {["Planning", "Ready for Production"].includes(job.status) && <Button variant="outline" onClick={() => navigate(`/production-planning/jobs/${id}/edit`)} data-testid="edit-job-btn"><Pencil className="mr-2 h-4 w-4" />Edit</Button>}
            {job.status === "Planning" && <Button variant="outline" onClick={() => move("Ready for Production")} data-testid="ready-job-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Mark Ready</Button>}
            {job.status === "Ready for Production" && <Button variant="outline" onClick={() => move("Planning")} data-testid="unready-job-btn">Back to Planning</Button>}
            {job.status === "Ready for Production" && <Button className="bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => move("Released")} data-testid="release-job-btn"><Rocket className="mr-2 h-4 w-4" />Release to Production</Button>}
            {job.status === "Released" && <Button variant="outline" onClick={() => move("In Production")} data-testid="inprod-job-btn"><Factory className="mr-2 h-4 w-4" />Mark In Production</Button>}
            {job.status === "In Production" && <Button variant="outline" onClick={() => move("Completed")} data-testid="complete-job-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Mark Completed</Button>}
            {job.status === "Completed" && <Button variant="outline" onClick={() => move("Closed")} data-testid="close-job-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Close</Button>}
            {["Planning", "Ready for Production", "Released"].includes(job.status) && <Button variant="outline" className="border-rose-300 text-rose-700" onClick={() => setCancelOpen(true)} data-testid="cancel-job-btn"><XCircle className="mr-2 h-4 w-4" />Cancel</Button>}
            {job.published && <Button variant="outline" onClick={() => navigate("/production")} data-testid="open-production-btn"><Factory className="mr-2 h-4 w-4" />Open Production</Button>}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-slate-900">Plan</h3>
          <div className="mt-4 grid gap-4 text-sm md:grid-cols-3">
            <div><p className="text-slate-500">Target</p><p className="font-medium text-slate-800">{job.target_meters} m</p></div>
            <div><p className="text-slate-500">Target date</p><p className="font-medium text-slate-800">{job.target_date || "—"}</p></div>
            <div><p className="text-slate-500">Sequence</p><p className="font-medium text-slate-800">{job.sequence || "—"}</p></div>
            <div><p className="text-slate-500">Est. hours</p><p className="font-medium text-slate-800">{job.estimated_hours || "—"}</p></div>
            <div><p className="text-slate-500">Supervisor</p><p className="font-medium text-slate-800">{job.supervisor_name || "—"}</p></div>
            <div><p className="text-slate-500">Wastage / Shrinkage</p><p className="font-medium text-slate-800">{job.allowed_wastage_pct}% / {job.allowed_shrinkage_pct}%</p></div>
          </div>
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wider text-slate-500">Route & machines</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(job.route || []).map((st) => (
                <Badge key={st} className="bg-slate-100 capitalize text-slate-700 hover:bg-slate-100" data-testid={`route-badge-${st}`}>{st} → {job.machine_assignments?.[st] || "unassigned"}</Badge>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wider text-slate-500">Reserved fabric rolls</p>
            <div className="mt-2 space-y-1">
              {(job.reserved_fabric_rolls || []).map((r, i) => (
                <div key={i} className="flex justify-between rounded-md bg-slate-50 px-3 py-1.5 text-sm" data-testid={`reserved-roll-${i}`}>
                  <span className="font-medium text-slate-800">{r.roll_id}</span><span className="text-slate-600">{r.meters} m</span>
                </div>
              ))}
              {(!job.reserved_fabric_rolls || job.reserved_fabric_rolls.length === 0) && <p className="text-sm text-slate-400">None reserved.</p>}
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">References</h3>
            <div className="mt-3 space-y-2 text-sm">
              <button className="block text-indigo-600 hover:underline" onClick={() => navigate(`/sales-orders/${job.so_id}`)}>Sales Order {job.so_no} →</button>
              <button className="block text-indigo-600 hover:underline" onClick={() => navigate(`/customers/${job.customer_id}`)}>Customer {job.customer?.company_name} →</button>
              {job.artwork_id && <button className="block text-indigo-600 hover:underline" onClick={() => navigate(`/artwork/${job.artwork_id}`)}>Artwork {a?.artwork_code} →</button>}
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Artwork reservation</h3>
            {a ? <div className="mt-3 text-sm"><p className="font-medium text-slate-800">{a.artwork_code} · {a.name}</p><p className="text-slate-500">Locked V{a.version}</p></div>
               : <p className="mt-3 text-sm text-amber-600">No artwork — printing will be blocked in Production.</p>}
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Line item</h3>
            <div className="mt-3 text-sm text-slate-700">
              <p className="font-medium">{job.item?.fabric_type}</p>
              <p className="text-slate-500">{[job.item?.gsm && `${job.item.gsm} GSM`, job.item?.width, job.item?.print_type].filter(Boolean).join(" · ")}</p>
              <p className="text-slate-500">{job.item?.quantity} {job.item?.unit}</p>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900">History</h3>
        <div className="mt-4 space-y-3">
          {[...(job.history || [])].reverse().map((h, i) => (
            <div key={i} className="flex gap-3 border-b border-slate-100 pb-3" data-testid="job-history-item">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" />
              <div><p className="text-sm text-slate-700"><span className="font-medium">{h.status}</span>{h.note ? ` · ${h.note}` : ""}</p><p className="text-xs text-slate-400">{h.by} · {new Date(h.at).toLocaleString()}</p></div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel job</DialogTitle><DialogDescription>Record why this production job is being cancelled.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="cancel-reason-input" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setCancelOpen(false)}>Keep job</Button><Button onClick={submitCancel} data-testid="confirm-cancel-btn" className="bg-rose-600 text-white hover:bg-rose-700">Cancel job</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
